import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Check, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL, WhatsAppIcon } from "@/components/site/ContactLinks";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGE_BRACKETS = [
  { value: "6–8 ans", key: "age.6-8" },
  { value: "9–11 ans", key: "age.9-11" },
  { value: "12–15 ans", key: "age.12-15" },
  { value: "Autre", key: "age.other" },
];

export default function TalkForm({ open, onClose }) {
  const { t, lang } = useLang();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [form, setForm] = useState({
    parent_first_name: "",
    parent_last_name: "",
    email: "",
    phone: "",
    commune: "",
    preferred_lang: lang,
    child_age_bracket: "",
    main_need: "",
    contact_preference: "email",
    availability: "",
    consent_contact: false,
    consent_privacy: false,
  });

  useEffect(() => {
    // Sync preferred_lang with current site lang when dialog opens
    if (open) {
      setForm((f) => ({ ...f, preferred_lang: lang }));
    } else {
      setTimeout(() => {
        setStep(1);
        setSuccess(false);
      }, 300);
    }
  }, [open, lang]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canGoStep2 = form.parent_first_name && form.parent_last_name && form.email;
  const canSubmit =
    canGoStep2 && form.child_age_bracket && form.main_need && form.consent_contact && form.consent_privacy;

  const NEEDS = [
    { id: "ecole", label: t("talk.need.ecole") },
    { id: "bouger", label: t("talk.need.bouger") },
    { id: "lien", label: t("talk.need.lien") },
    { id: "creer", label: t("talk.need.creer") },
    { id: "orientation", label: t("talk.need.orientation") },
  ];
  const CONTACT_PREF = [
    { id: "email", label: t("talk.pref.email") },
    { id: "phone", label: t("talk.pref.phone") },
  ];

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await axios.post(`${API}/talk-requests`, form);
      setSuccess(true);
    } catch (err) {
      toast.error(t("talk.errors.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] bg-foreground/60 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6"
          onClick={onClose}
          data-testid="talk-dialog"
        >
          <motion.div
            initial={{ y: 60, opacity: 0.8 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full md:max-w-2xl bg-background rounded-t-3xl md:rounded-3xl max-h-[92vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-background/95 backdrop-blur px-6 md:px-10 py-5 flex items-center justify-between border-b border-border z-10">
              <div>
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-primary">
                  {t("talk.title")}
                </div>
                <div className="mt-1 font-heading text-xl">
                  {success ? t("talk.success_title") : step === 1 ? t("talk.step1_label") : t("talk.step2_label")}
                </div>
              </div>
              <button
                onClick={onClose}
                data-testid="talk-close-btn"
                aria-label={t("talk.close")}
                className="p-2.5 rounded-full border border-border hover:bg-muted"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 md:px-10 py-8">
              {!success && (
                <div
                  className="mb-6 rounded-2xl bg-muted/70 border border-border p-4 flex flex-wrap items-center gap-3"
                  data-testid="talk-direct-contact"
                >
                  <span className="text-sm font-semibold text-foreground/75">{t("talk.direct")}</span>
                  <a
                    href={PHONE_TEL}
                    data-testid="talk-phone-link"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90"
                  >
                    <Phone className="w-4 h-4" strokeWidth={2.5} />
                    {PHONE_DISPLAY}
                  </a>
                  <a
                    href={WHATSAPP_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="talk-whatsapp-link"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white hover:opacity-90"
                    style={{ backgroundColor: "#25D366" }}
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                    WhatsApp
                  </a>
                </div>
              )}
              {success ? (
                <SuccessState onClose={onClose} t={t} />
              ) : (
                <form onSubmit={submit} className="space-y-6" data-testid="talk-form">
                  {step === 1 && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label={t("talk.first_name")} value={form.parent_first_name} onChange={(v) => update("parent_first_name", v)} testId="talk-first-name" required />
                        <TextField label={t("talk.last_name")} value={form.parent_last_name} onChange={(v) => update("parent_last_name", v)} testId="talk-last-name" required />
                      </div>
                      <TextField label={t("talk.email")} type="email" value={form.email} onChange={(v) => update("email", v)} testId="talk-email" required />
                      <TextField label={t("talk.phone")} type="tel" value={form.phone} onChange={(v) => update("phone", v)} testId="talk-phone" placeholder="+32 …" />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <TextField label={t("talk.commune")} value={form.commune} onChange={(v) => update("commune", v)} testId="talk-commune" />
                        <div>
                          <Label>{t("talk.preferred_lang")}</Label>
                          <div className="flex gap-2 mt-2">
                            {["fr", "nl"].map((l) => (
                              <button
                                key={l}
                                type="button"
                                onClick={() => update("preferred_lang", l)}
                                data-testid={`talk-lang-${l}`}
                                className={`px-4 py-2.5 rounded-full font-bold text-sm border-2 min-h-[44px] ${
                                  form.preferred_lang === l ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-foreground"
                                }`}
                              >
                                {l === "fr" ? "Français" : "Nederlands"}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="pt-4 flex flex-col sm:flex-row sm:justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => canGoStep2 && setStep(2)}
                          disabled={!canGoStep2}
                          data-testid="talk-next-btn"
                          className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 justify-center"
                        >
                          {t("talk.next")}
                        </button>
                      </div>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-5">
                      <div>
                        <Label>{t("talk.age")}</Label>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {AGE_BRACKETS.map((a) => (
                            <button
                              key={a.value}
                              type="button"
                              onClick={() => update("child_age_bracket", a.value)}
                              data-testid={`talk-age-${a.value.replace(/[^\w]/g, "-")}`}
                              className={`px-4 py-2.5 rounded-full font-semibold text-sm border-2 min-h-[44px] ${
                                form.child_age_bracket === a.value ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-foreground"
                              }`}
                            >
                              {t(a.key)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label>{t("talk.need")}</Label>
                        <div className="mt-2 grid grid-cols-1 gap-2">
                          {NEEDS.map((n) => (
                            <button
                              key={n.id}
                              type="button"
                              onClick={() => update("main_need", n.id)}
                              data-testid={`talk-need-${n.id}`}
                              className={`text-left px-4 py-3 rounded-2xl font-medium text-sm border-2 ${
                                form.main_need === n.id ? "bg-primary/5 border-primary text-foreground" : "bg-white border-border text-foreground/85 hover:border-foreground/25"
                              }`}
                            >
                              {n.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <Label>{t("talk.contact_pref")}</Label>
                        <div className="mt-2 flex gap-2">
                          {CONTACT_PREF.map((c) => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => update("contact_preference", c.id)}
                              data-testid={`talk-pref-${c.id}`}
                              className={`px-4 py-2.5 rounded-full font-semibold text-sm border-2 min-h-[44px] ${
                                form.contact_preference === c.id ? "bg-primary text-primary-foreground border-primary" : "bg-white border-border text-foreground"
                              }`}
                            >
                              {c.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <TextField label={t("talk.availability")} value={form.availability} onChange={(v) => update("availability", v)} testId="talk-availability" placeholder={t("talk.avail_placeholder")} />

                      <div className="space-y-3 pt-2">
                        <Checkbox label={t("talk.consent_contact")} checked={form.consent_contact} onChange={(v) => update("consent_contact", v)} testId="talk-consent-contact" />
                        <Checkbox label={t("talk.consent_privacy")} checked={form.consent_privacy} onChange={(v) => update("consent_privacy", v)} testId="talk-consent-privacy" />
                      </div>

                      <div className="pt-4 flex flex-col sm:flex-row justify-between gap-3">
                        <button type="button" onClick={() => setStep(1)} data-testid="talk-back-btn" className="pill-btn bg-white border-2 border-foreground text-foreground justify-center">
                          {t("talk.back")}
                        </button>
                        <button type="submit" disabled={!canSubmit || busy} data-testid="talk-submit-btn" className="pill-btn bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 justify-center">
                          <Send className="w-4 h-4" strokeWidth={2.5} />
                          {busy ? t("talk.submitting") : t("talk.submit")}
                        </button>
                      </div>

                      <p className="text-xs text-foreground/55 leading-relaxed">{t("talk.no_medical")}</p>
                    </div>
                  )}
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Label({ children }) { return <label className="block text-sm font-bold text-foreground">{children}</label>; }

function TextField({ label, value, onChange, testId, type = "text", required, placeholder }) {
  return (
    <div>
      <Label>{label} {required && "*"}</Label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={`${testId}-input`}
        className="mt-2 w-full px-4 py-3 rounded-2xl border-2 border-border bg-white outline-none focus:border-primary min-h-[48px]"
      />
    </div>
  );
}

function Checkbox({ label, checked, onChange, testId }) {
  return (
    <label
      className="flex items-start gap-3 cursor-pointer group"
      data-testid={testId}
      onClick={(e) => {
        if (e.target.tagName !== "INPUT") {
          e.preventDefault();
          onChange(!checked);
        }
      }}
    >
      <span className={`shrink-0 w-6 h-6 mt-0.5 rounded-md border-2 grid place-items-center transition-colors ${
        checked ? "bg-primary border-primary" : "bg-white border-border group-hover:border-foreground/40"
      }`}>
        {checked && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
      </span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="sr-only" />
      <span className="text-sm text-foreground/85 leading-relaxed">{label}</span>
    </label>
  );
}

function SuccessState({ onClose, t }) {
  return (
    <div className="text-center py-6" data-testid="talk-success">
      <div className="mx-auto w-20 h-20 rounded-full grid place-items-center" style={{ backgroundColor: "hsl(var(--accent))" }}>
        <Check className="w-10 h-10 text-foreground" strokeWidth={3} />
      </div>
      <h3 className="mt-6 font-heading text-2xl md:text-3xl">{t("talk.success_title")}</h3>
      <p className="mt-3 text-foreground/75 leading-relaxed max-w-md mx-auto">{t("talk.success_text")}</p>
      <button onClick={onClose} className="mt-8 pill-btn bg-primary text-primary-foreground hover:bg-primary/90">
        {t("talk.success_back")}
      </button>
    </div>
  );
}
